import * as React from 'react';
import { SecondaryAlert } from 'kagr-ui';
import * as styles from './view-segment.module.scss';
import { CollapsibleSection } from '../common/collapsible-section';
import SegmentResultsView from './segment-results/segment-results-view';
import ViewSegmentDetails from './view-segment/view-segment-details';
import ViewSegmentAttributes from './view-segment/view-segment-attributes';
import ExportSegment from './export-segment/export-segment';
import { SegmentAttributeOperator, SegmentDetailResponseParsed } from '../../models/segments';
import { makeStyles } from '@material-ui/core';
import { UseQueryResult } from '@tanstack/react-query';

const useStyles = makeStyles(() => ({
  divider: {
    height: '25px',
    borderRight: '1px solid #979797',
    margin: '5px 0'
  }
}));

type ViewSegmentProps = {
  segmentId: string;
  segmentQuery: UseQueryResult<SegmentDetailResponseParsed, Error>;
  powerUser: boolean;
  userName: string;
};

/**
 * View a single existing segment without editing
 */
const ViewSegment = ({ segmentId, segmentQuery, powerUser, userName }: ViewSegmentProps) => {
  const classes = useStyles();
  const [detailsCollapsed, setDetailsCollapsed] = React.useState<boolean>(false);
  const [buildCollapsed, setBuildCollapsed] = React.useState<boolean>(false);

  return (
    <div className={styles.root}>
      {segmentQuery.isError && (
        <div className={styles.error}>
          <SecondaryAlert type="error" message={segmentQuery.error.message} />
        </div>
      )}
      <CollapsibleSection title="Segment Details" collapsed={detailsCollapsed} onCollapse={setDetailsCollapsed}>
        {segmentQuery.data && <ViewSegmentDetails segmentId={segmentId} />}
      </CollapsibleSection>
      <CollapsibleSection
        title="Summary"
        collapsed={buildCollapsed}
        onCollapse={setBuildCollapsed}
        className={styles.buildSection}
        secondaryAction={
          <React.Fragment>
            {powerUser && (
              <>
                <div className={classes.divider}></div>
                <ExportSegment
                  name={segmentQuery.data ? segmentQuery.data.name : 'Untitled'}
                  includedAttributes={
                    segmentQuery.data
                      ? segmentQuery.data.selectedAttributes.include
                      : { operator: SegmentAttributeOperator.And, predicates: [] }
                  }
                  excludedAttributes={
                    segmentQuery.data
                      ? segmentQuery.data.selectedAttributes.exclude
                      : { operator: SegmentAttributeOperator.And, predicates: [] }
                  }
                  segmentId={segmentId}
                  segmentShared={segmentQuery.data.isShared ? 'Share' : 'Do not share'}
                  userName={userName}
                />
              </>
            )}
          </React.Fragment>
        }
      >
        <SegmentResultsView />
        {segmentQuery.data && (
          <ViewSegmentAttributes
            includedAttributes={segmentQuery.data.attributes.include}
            excludedAttributes={segmentQuery.data.attributes.exclude}
            includeComposition={segmentQuery.data.composition?.include}
            excludeComposition={segmentQuery.data.composition?.exclude}
          />
        )}
      </CollapsibleSection>
    </div>
  );
};

export default ViewSegment;
